/**
 * Which language this browser reads the game in (T124).
 *
 * ⚠️ **There is only English today, so this changes nothing you can see.** It is
 * here because *where the preference lives* is the kind of decision that is
 * awkward to change once something depends on it, and deciding it now costs one
 * small file.
 *
 * ## Why the browser and not the account
 *
 * The world is shared and the family is two to ten people (D1, D3), but the
 * language somebody reads is about **their screen**, not about their character —
 * and one of them may well want Icelandic on the laptop and English on the
 * desktop. Keeping it here means no database column, no migration, no protocol
 * field, and nothing for the server to know.
 *
 * 🔴 **If it should follow the account instead, that is a real decision and it
 * belongs to haugur.** Moving it later is a column on `players` and one field on
 * the welcome — a morning's work, not a redesign — which is exactly why it is
 * safe to start here.
 */

import { DEFAULT_LANGUAGE, availableLanguages, setLanguage } from '@lofi/shared';

/** Where the choice is remembered. */
const STORED_AS = 'lofi.language';

/**
 * Choose the language, best match first: what was chosen before, then what the
 * browser asks for, then English.
 *
 * ⚠️ **`navigator.language` is a full tag** — `is-IS`, `en-GB` — and our files
 * are named by the bare language, so the region is trimmed before matching. A
 * German who has never chosen anything gets English, because there is no German
 * file, and {@link setLanguage} says so by returning `false` rather than throwing.
 */
export function chooseLanguage(): string {
  for (const candidate of preferences()) {
    if (setLanguage(candidate)) return candidate;
  }
  return DEFAULT_LANGUAGE;
}

/** Remember a choice, and use it. Returns `false` if there is no such file. */
export function rememberLanguage(code: string): boolean {
  if (!setLanguage(code)) return false;
  try {
    localStorage.setItem(STORED_AS, code);
  } catch {
    /* Private browsing, or storage full. Not being able to remember the choice
       is no reason to refuse to make it. */
  }
  return true;
}

/** The languages there are files for — what a picker would offer, when there is one. */
export function offeredLanguages(): readonly string[] {
  return availableLanguages();
}

function preferences(): readonly string[] {
  const out: string[] = [];

  try {
    const stored = localStorage.getItem(STORED_AS);
    if (stored) out.push(stored);
  } catch {
    /* See above. */
  }

  /* ⚠️ `languages` before `language`: a browser set to Icelandic first and
     English second should get Icelandic, and `language` alone only ever reports
     the first one. */
  for (const tag of [...(navigator.languages ?? []), navigator.language]) {
    if (typeof tag !== 'string' || tag.length === 0) continue;
    out.push(tag);
    const bare = tag.split('-')[0];
    if (bare !== undefined && bare !== tag) out.push(bare);
  }

  out.push(DEFAULT_LANGUAGE);
  return out;
}
